
import {defineComponent, ref, inject, watchEffect, onMounted, onUnmounted} from "vue";
import { useStore } from "vuex";
import { Apollo, Notify } from "@/core/services";
import { GET_TAX, SEARCH_TAX } from "../graphql/Queries";
import { DELETE_TAX } from "../graphql/Mutations";
import { useI18n } from "vue-i18n";
import Search from "@/components/search/Search.vue";
import Table from "../../../../components/Table/Table.vue";
import InnerLoader from "../../../../components/InnerLoader.vue";
import { dropdown_handler } from "../../../../core/helpers/dropdownHandler";

export default defineComponent({
	name: "taxes-list",
	components: { Search, Table, InnerLoader },
	setup() {
		const store = useStore();
		const i18n = useI18n();
		const emitter: any = inject("emitter");
		const taxes = ref([]);
		const system_locale = ref();
		const pagination = ref({}) as Record<any, any>;
		const loader = ref(false);
		const loading = ref(false);
		const clickHandler = ref() as Record<any, any>;
		const currentPage = ref();
		const permissions = store.getters.getPermissions;
		const activeIndex = ref(-1);
    const searchObservable: any = ref(null);
    const observable: any = ref(null);
		const columns = ref([
			{
				label: "message.NAME",
				key: "name",
			},
			{
				label: "message.TYPE",
				key: "rate_type",
			},
			{
				label: "message.RATE",
				key: "rate",
			},
			{
				label: "message.CREATED_AT",
				key: "created_at",
			},
			{
				label: "message.ACTIONS",
				key: "actions",
				textAlignment: "center",
			},
		]);

		const dropdownHandler = (index, id) => {
			activeIndex.value = index === id ? null : id;
		};
		const handleClickOutside = (event) => {
			if (!event.target.closest(".action-btn")) {
				activeIndex.value = -1;
			}
		};

		const get_tax = (page = 1, reload = false) => {
      if (searchObservable.value != null) {
        searchObservable.value?.unsubscribe();
        searchObservable.value = null;
      }
      if(page == 1) observable.value?.unsubscribe();
      loader.value = true;
      observable.value = Apollo.watchQuery({
				query: GET_TAX,
				fetchPolicy: "network-only",
				nextFetchPolicy: "cache-and-network",
				errorPolicy: "all",
				variables: {
					page: page,
					limit: 10,
				},
			}).subscribe(({ data, errors }) => {
				if (errors) {
					loader.value = false;
				}
				reload == true ? (currentPage.value = 1) : false;
				clickHandler.value = get_tax;
				taxes.value = data.taxes.data;
				pagination.value = data.taxes;
				loader.value = false;
			});
		};

		const handleCurrentChange = (page: number) => {
			clickHandler.value(page);
		};

		get_tax(1);

		const handleEdit = (tax: Record<any, any>) => {
      emitter.emit("editTaxAction", tax);
		};

		watchEffect(() => {
			system_locale.value = i18n.fallbackLocale.value;
		});

    const searchHandler = (page = 1) => {
      if(observable.value != null) {
        observable.value?.unsubscribe();
        observable.value = null
      }
      searchObservable.value?.unsubscribe();
      const searchData = store.getters.getSearchData;
      loader.value = true;
      searchObservable.value = Apollo.watchQuery({
        query: SEARCH_TAX,
        errorPolicy: "all",
        fetchPolicy: "network-only",
        nextFetchPolicy: "network-only",
        variables: {
          page: page,
          limit: 10,
          search_key: searchData.data.input,
          locale: system_locale.value,
        },
      }).subscribe(({ data, errors }) => {
        if (errors) {
          loader.value = false;
        }

        clickHandler.value = searchHandler;
        taxes.value = data.search_taxes.data;
        pagination.value = data.search_taxes;
        loader.value = false;
      });
    };

		const handleDelete = (id: number) => {
			store.getters.appInstance.$messageBox
				.confirm(`${i18n.t("message.ARE_YOU_SURE_TO_DELETE_RECORD")}?`, i18n.t("message.INFO"), {
					confirmButtonText: i18n.t("message.OK"),
					cancelButtonText: i18n.t("message.CANCEL"),
					type: "info",
				})
				.then(async () => {
					loader.value = true;
					await Apollo.mutate({
						mutation: DELETE_TAX,
						variables: { id: id },
						update: (store, { data: { delete_tax } }) => {
              if (searchObservable.value != null) searchHandler(pagination.value.current_page)
              else get_tax(pagination.value.current_page)
							loader.value = false;
						},
					});
					Notify.success(i18n.t("message.RECORD_DELETED_SUCCESSFULLY"));
				})
				.catch(() => {
					loader.value = false;
				});
		};

		onMounted(() => {
			document.addEventListener("click", handleClickOutside);
		});

		const exportFile = (data) => {
			const searchData = store.getters.getSearchData;
			loading.value = true;
			const sub = Apollo.watchQuery({
				query: SEARCH_TAX,
				errorPolicy: "all",
				fetchPolicy: "network-only",
				variables: {
					page: 0,
					limit: 1,
					filter: searchData?.data?.select ? searchData?.data?.select : "",
					search_key: searchData?.data?.input ? searchData?.data?.input : "",
					locale: system_locale.value,
					type: data?.type,
					date_from: data?.dateFrom,
					date_to: data?.dateTo,
				},
			}).subscribe(({ data, errors }) => {
				if (errors) {
					loading.value = false;
				} else if (data) {
					window.open(data?.search_taxes?.data[0]?.path, "_blank");
					loading.value = false;
				}

				loading.value = false;
				sub.unsubscribe();
			});
		};

    onUnmounted(() => {
      observable.value?.unsubscribe();
      searchObservable.value?.unsubscribe();
    });

		return {
			taxes,
			pagination,
			system_locale,
			loading,
			currentPage,
			columns,
			permissions,
			loader,
			exportFile,
			handleCurrentChange,
			get_tax,
			handleEdit,
			handleDelete,
			searchHandler,
			activeIndex,
			dropdownHandler,
			handleClickOutside,
		};
	},
});
